import React, { useCallback, useContext, useState, useEffect } from 'react';

import { Icon } from 'design-system-web';
import cx from 'classnames';
import { Button } from 'components';
import { useTranslation } from 'react-i18next';

import { ContentRendererContext, UserDataContext } from 'lane-shared/contexts';
import {
  ContentType,
  VisitorManagementFeatureProperties,
  type VisitorType,
} from 'application-types';
import { ICON_SET_FONTAWESOME } from 'lane-shared/helpers/constants/icons';
import { useVisitorsList, useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';

import { H5 } from 'components/typography';

import history from 'helpers/history';
import { getVisitorManagementFeatures } from 'domains/visitorManagement/types/visitorFormInteraction';
import { isVisitorEmailConflict } from 'lane-shared/domains/visitorManagement/helpers/isVisitorEmailConflict';
import {
  VisitorInviteForm,
  VisitorManagementVisitorListItems,
} from 'domains/visitorManagement/components';
import { BulkVisitorsItem } from 'domains/visitorManagement/components/BulkVisitosItem/BulkVisitorsItem';
import { DownloadVisitorInfoSample } from 'domains/visitorManagement/components/DownloadVisitorInfoSample';
import LeaveConfirmationPrompt from 'domains/visitorManagement/components/LeaveConfirmationPrompt/LeaveConfirmationPrompt';
import { VisitorInviteBulkUpload } from 'domains/visitorManagement/components/VisitorInviteBulkUpload/VisitorInviteBulkUpload';

import styles from 'domains/visitorManagement/content-blocks/AddVisitorsBlock.scss';

import { VisitorInviteBulkUploadLegacy } from 'domains/visitorManagement/components/VisitorInviteBulkUpload/VisitorInviteBulkUploadLegacy';
import { useFlatfileUpgradeToPlatformEnabled } from 'react-hooks';
import { UploadFileWrapper } from 'domains/visitorManagement/components/VisitorInviteBulkUpload/UploadFileWrapper';
import { useFormCustomFieldColumns } from 'domains/visitorManagement/hooks/useFormCustomFieldColumns';

export default function AddVisitorsBlock({
  className,
  onChange,
  properties,
  value,
  style,
  show,
  isPreview = false,
  content,
  submissionCompletedAt,
  submitAttempted,
  hostTenantId,
  hostEmail: selectedHostEmail,
}: {
  className?: string;
  onChange: (value: VisitorType[]) => void;
  properties: VisitorManagementFeatureProperties;
  value: VisitorType[] | null;
  style?: React.CSSProperties;
  show: boolean;
  isPreview?: boolean;
  content?: ContentType;
  submissionCompletedAt?: Date;
  submitAttempted?: boolean;
  hostTenantId?: string;
  hostEmail?: string;
}) {
  const { t } = useTranslation();

  const [isPristine, setIsPristine] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const { onInteractionUpdated, interaction, isVisitorLogView } = useContext(
    ContentRendererContext
  );
  const { user } = useContext(UserDataContext);
  const [existingBulkVisitors, setExistingBulkVisitors] = useState(
    [] as VisitorType[]
  );

  const isBulkImportEnabled =
    properties.bulkImportSetting === undefined
      ? true
      : properties.bulkImportSetting;

  function onBulkVisitorsChange(bulkVisitors: VisitorType[]) {
    setExistingBulkVisitors(bulkVisitors);
  }

  const {
    visitors,
    handleAdd,
    handleBulkAdd: handleBulkAddFromList,
    handleEditSave,
    handleDelete,
    handleBulkDelete,
    resetForm,
  } = useVisitorsList({
    visitors: value,
    bulkVisitors: existingBulkVisitors,
    onBulkVisitorsChange,
    onChange,
    contentType: content?.type,
    channelId: content?.channel?._id,
  });

  const handleBulkAdd = useCallback(
    (bulkVisitors: VisitorType[]) => {
      handleBulkAddFromList(bulkVisitors);
      onInteractionUpdated?.({
        features: {
          VisitorManagement: { uploadedViaBulkImport: true },
        },
      } as Parameters<NonNullable<typeof onInteractionUpdated>>[0]);
    },
    [handleBulkAddFromList, onInteractionUpdated]
  );

  const visitorInviteBulkUploadFlag = useFlag(
    FeatureFlag.VisitorInviteBulkUpload,
    false
  );

  const isVisitorEmailConflictValidationEnabled = useFlag(
    FeatureFlag.VisitorEmailConflictValidation,
    false
  );
  const submitterEmail = isVisitorEmailConflictValidationEnabled
    ? user?.profile?.email
    : undefined;
  // When the submitter is also the host, the submitter check already blocks
  // their email, so drop the host email to show "belongs to you" instead of
  // "belongs to the host".
  const hostEmail =
    isVisitorEmailConflictValidationEnabled &&
    !isVisitorEmailConflict(selectedHostEmail, submitterEmail)
      ? selectedHostEmail
      : undefined;

  const flatfileUpgradeToPlatformEnabled =
    useFlatfileUpgradeToPlatformEnabled();

  const visitorManagementCustomFieldsEnabled = useFlag(
    FeatureFlag.VisitorManagementCustomFields,
    false
  );

  const customFieldConfigs = visitorManagementCustomFieldsEnabled
    ? properties.customFieldConfigs
    : undefined;

  const { formCustomFieldColumns } = useFormCustomFieldColumns(
    customFieldConfigs,
    visitorManagementCustomFieldsEnabled ? properties.customFieldIds : undefined
  );

  const downloadSampleCustomFieldColumns =
    flatfileUpgradeToPlatformEnabled && visitorManagementCustomFieldsEnabled
      ? formCustomFieldColumns
      : undefined;

  useEffect(() => {
    setShowInviteForm(false);
  }, [visitors.length]);

  useEffect(() => {
    resetForm();
  }, [submissionCompletedAt]);

  useEffect(() => {
    const nowEmpty = existingBulkVisitors.length === 0;
    const persistedBulkFlag = Boolean(
      getVisitorManagementFeatures(interaction)?.uploadedViaBulkImport
    );

    if (nowEmpty && persistedBulkFlag) {
      onInteractionUpdated?.({
        features: {
          VisitorManagement: { uploadedViaBulkImport: false },
        },
      } as Parameters<NonNullable<typeof onInteractionUpdated>>[0]);
    }
  }, [existingBulkVisitors.length, interaction, onInteractionUpdated]);

  useEffect(() => {
    const hasSubmitterEmailConflict = [...visitors, ...existingBulkVisitors].some(
      visitor => isVisitorEmailConflict(visitor.email, submitterEmail)
    );
    const persistedHasSubmitterEmailConflict = Boolean(
      getVisitorManagementFeatures(interaction)?.hasSubmitterEmailConflict
    );

    if (hasSubmitterEmailConflict === persistedHasSubmitterEmailConflict) {
      return;
    }

    onInteractionUpdated?.({
      features: { VisitorManagement: { hasSubmitterEmailConflict } },
    } as Parameters<NonNullable<typeof onInteractionUpdated>>[0]);
  }, [
    visitors,
    existingBulkVisitors,
    submitterEmail,
    interaction,
    onInteractionUpdated,
  ]);

  useEffect(() => {
    const hasHostEmailConflict = [...visitors, ...existingBulkVisitors].some(
      visitor => isVisitorEmailConflict(visitor.email, hostEmail)
    );
    const persistedHasHostEmailConflict = Boolean(
      getVisitorManagementFeatures(interaction)?.hasHostEmailConflict
    );

    if (hasHostEmailConflict === persistedHasHostEmailConflict) {
      return;
    }

    onInteractionUpdated?.({
      features: { VisitorManagement: { hasHostEmailConflict } },
    } as Parameters<NonNullable<typeof onInteractionUpdated>>[0]);
  }, [
    visitors,
    existingBulkVisitors,
    hostEmail,
    interaction,
    onInteractionUpdated,
  ]);

  if (!show) return null;

  return (
    <>
      <LeaveConfirmationPrompt
        testId="visitorLeaveConfirmation"
        when={!submitAttempted && (visitors.length > 0 || !isPristine)}
        navigate={path => history.push(path)}
      />
      <div className={cx(className, styles.addVisitorsBlock)} style={style}>
        <H5 className={styles.visitorInviteTitle}>
          {t('web.content.feature.visitorManagement.visitorInvite.form.title')}
        </H5>
        <div className={styles.guestListContainer}>
          {Boolean(visitors.length) && (
            <VisitorManagementVisitorListItems
              visitors={visitors}
              properties={properties}
              allowEdit
              onEditSave={handleEditSave}
              onDelete={handleDelete}
              submitterEmail={submitterEmail}
              hostEmail={hostEmail}
            />
          )}
        </div>
        <div className={styles.guestListContainer}>
          {Boolean(existingBulkVisitors.length) && (
            <BulkVisitorsItem
              bulkVisitors={existingBulkVisitors}
              onDelete={handleBulkDelete}
              submitterEmail={submitterEmail}
              hostEmail={hostEmail}
            />
          )}
        </div>
        <br />
        {Boolean(visitors.length === 0 || showInviteForm) && (
          <VisitorInviteForm
            onAdd={handleAdd}
            value={visitors}
            bulkVisitors={existingBulkVisitors}
            properties={properties}
            onFieldChange={(changed: boolean) => setIsPristine(changed)}
            isPreview={isPreview}
            tenantId={hostTenantId}
            customFieldConfigs={customFieldConfigs}
            isVisitorLogView={isVisitorLogView}
            submitterEmail={submitterEmail}
            hostEmail={hostEmail}
          />
        )}

        <div className={styles.buttons}>
          {Boolean(visitors.length !== 0) && !showInviteForm && (
            <Button
              onClick={() => setShowInviteForm(true)}
              disabled={isPreview}
              className={styles.addButton}
              testId="addAnotherVisitor"
              startIcon={
                <Icon
                  name="plus-circle"
                  testId="deleteIcon"
                  set={ICON_SET_FONTAWESOME}
                />
              }
            >
              {t(
                'web.content.feature.visitorManagement.visitorInvite.form.addAnother'
              )}
            </Button>
          )}
          {isBulkImportEnabled &&
            (visitorInviteBulkUploadFlag || Boolean(visitors.length !== 0)) ? (
            <div className={styles.bulkUploadSection}>
              {flatfileUpgradeToPlatformEnabled ? (
                <UploadFileWrapper>
                  <VisitorInviteBulkUpload
                    properties={properties}
                    handleBulkAdd={handleBulkAdd}
                    isPreview={isPreview}
                    formCustomFieldColumns={formCustomFieldColumns}
                    existingVisitors={[...visitors, ...existingBulkVisitors]}
                  />
                </UploadFileWrapper>
              ) : (
                <VisitorInviteBulkUploadLegacy
                  handleBulkAdd={handleBulkAdd}
                  properties={properties}
                  isPreview={isPreview}
                />
              )}

              <DownloadVisitorInfoSample
                isPreview={isPreview}
                formCustomFieldColumns={downloadSampleCustomFieldColumns}
              />
            </div>
          ) : null}
        </div>
      </div>
    </>
  );
}
